import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { InfoOutlined } from '@mui/icons-material';
import { compose } from 'recompose';
import { observer } from 'mobx-react';

import {
  LeadHistoryType,
  LeadIntentFlagLabel,
  LeadState,
} from '../../../../../../../Model/Lead';
import { CallTypeLabel } from '../../../../CallOutcomeModal/utils';
import { DateFormat } from '../../../../../../../../utils/date';
import statesTexts from '../../../../../states.json';
import salesStageTexts from '../../../../../salesStages.json';
import outcomeReasons from '../../../../../outcomeReasons.json';
import MsgModal from '../../../../MsgModal';
import { formatText } from '../../../../../../../utils/textUtils';
import texts from './texts.json';
import {
  Container,
  RowContainer,
  NoteCredit,
  Separator,
  StyledTypography,
  ColumnContainer,
  StrongTypography,
  StyledInfoButton,
  NoteText,
  StyledDate,
} from './styles';

const allOutcomeReasons = { ...outcomeReasons.lost, ...outcomeReasons.followUp };

const Note = ({
  type,
  value,
  reason,
  note,
  date,
  name,
  createdUserId,
  coachId,
}) => {
  const [showMsgModal, setShowMsgModal] = useState(false);
  const momentDate = useMemo(() => moment(date.toDate()), [date]);
  const dateDifference = useMemo(() => momentDate.fromNow(), [momentDate]);

  const historyText = useMemo(() => {
    let provider = '';
    let message = '';
    switch (type) {
      case LeadHistoryType.STATE_CHANGE:
        return value === LeadState.NEW ? texts.newLeadCreated
          : format(texts[type], { value: statesTexts[value] || value });
      case LeadHistoryType.SALES_STAGE_CHANGE:
        return format(texts[type], {
          value: salesStageTexts[value] || value,
          reason: allOutcomeReasons[reason] || reason,
        });
      case LeadHistoryType.MSG:
        if (
          value.msgTemplate && [
            salesStageTexts.FIRST_MESSAGE,
            salesStageTexts.SECOND_MESSAGE,
            salesStageTexts.THIRD_MESSAGE,
          ].includes(value?.msgTemplate?.name)
        ) {
          message = value.msgTemplate.name;
        } else {
          message = texts.message;
        }
        return formatText(format(texts[type], { message }));
      case LeadHistoryType.CALL:
        return format(texts[type], { duration: value.callDuration });
      case LeadHistoryType.CALL_OUTCOME:
        return format(texts[type], {
          outcome: salesStageTexts[value?.outcome] || salesStageTexts[value] || value?.outcome || value,
        });
      case LeadHistoryType.INTENT_FLAG_CHANGE:
        return format(texts[type], { value: LeadIntentFlagLabel[value] || value });
      case LeadHistoryType.CALL_PLACED:
        return format(texts[type], {
          isName: name,
          date: moment(value.callTime.toDate()).format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT),
        });
      case LeadHistoryType.CALENDLY_SCHEDULED:
        if (value.isCalDotComCall) {
          return format(texts[type].caldotcom, {
            duration: moment(value.endTime.toDate()).diff(moment(value.startTime.toDate()), 'minutes'),
            startTime: moment(value.startTime.toDate()).format(DateFormat.DATE_MONTH_TIME_SHORT_DISPLAY_FORMAT),
          });
        }
        return format(texts[type].calendly, {
          isName: value.isUserName,
          duration: moment(value.endTime.toDate()).diff(moment(value.startTime.toDate()), 'minutes'),
          startTime: moment(value.startTime.toDate()).format(DateFormat.DATE_MONTH_TIME_SHORT_DISPLAY_FORMAT),
        });
      case LeadHistoryType.RESCHEDULED_MANUALLY:
        return format(texts[type], {
          isName: value.isUserName,
          callTime: moment(value.callTime.toDate()).format(DateFormat.DATE_MONTH_TIME_SHORT_DISPLAY_FORMAT),
        });
      case LeadHistoryType.DIRECT_PURCHASE_LINK_SENT:
        if (value.smsSent && value.emailSent) {
          provider = texts.emailAndSms;
        } else if (value.smsSent) {
          provider = texts.sms;
        } else {
          provider = texts.email;
        }
        return format(texts[type], { provider });
      case LeadHistoryType.CALL_CANCELLED:
        return format(texts[type], { reason });
      case LeadHistoryType.SENT_TO_RESCHEDULE:
        return format(texts[type], { reason });
      case LeadHistoryType.MSG_SENT:
        return format(texts[type]);
      case LeadHistoryType.COMMENT:
        return format(texts[type], { comment: value });
      case LeadHistoryType.RESPONSE:
        return format(texts[type]);
      case LeadHistoryType.NOTE:
        return coachId === createdUserId
          ? format(texts.coachNote, { note: value })
          : value;
      default:
        return value;
    }
  }, [
    type,
    value,
    reason,
    name,
    coachId,
    createdUserId,
  ]);

  return (
    <Container>
      {showMsgModal && (
        <MsgModal
          open={showMsgModal}
          onClose={() => setShowMsgModal(false)}
          contactNo={value.contactNo}
          provider={value.msgProvider}
          msgTemplate={{
            ...value.msgTemplate,
            msgBody: value.messageBody,
          }}
          sendAsUserName={value.sendAsUserName}
        />
      )}
      <RowContainer>
        <StyledDate>
          {momentDate.format(DateFormat.DATE_MONTH_TIME_SHORT_DISPLAY_FORMAT)}
        </StyledDate>
        <Separator>
          {texts.separator}
        </Separator>
        <ColumnContainer>
          <StyledTypography>
            {historyText}
            {type === LeadHistoryType.MSG && (
              <StyledInfoButton size="small" onClick={() => setShowMsgModal(true)}>
                <InfoOutlined fontSize="small" />
              </StyledInfoButton>
            )}
          </StyledTypography>
          {type === LeadHistoryType.CALL_OUTCOME && !!reason && (
            // Existing notes are saved as reason in the database
            <NoteText>{format(texts.reason, { reason: allOutcomeReasons[reason] || reason })}</NoteText>
          )}
          {type === LeadHistoryType.CALL_OUTCOME && !!note && (
            <NoteText>{format(texts.note, { note })}</NoteText>
          )}
          {type === LeadHistoryType.CALL_OUTCOME && !!value && typeof value === 'object'
            && !!value.attemptCount && !!value.attemptType && (
            <NoteText>
              {format(texts.attempt, {
                attemptCount: value.attemptCount,
                attemptType: CallTypeLabel[value.attemptType] || value.attemptType,
              })}
            </NoteText>
          )}
          {type === LeadHistoryType.CALL_OUTCOME && typeof value === 'object' && !!value?.followUpDate && (
            <NoteText>
              {format(texts.followUpDateTime, {
                date: moment(value.followUpDate.toDate()).format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT),
              })}
            </NoteText>
          )}
          <NoteCredit>
            <StrongTypography>
              {dateDifference}
            </StrongTypography>
            {formatText(format(texts.userName, {
              name,
            }))}
          </NoteCredit>
        </ColumnContainer>
      </RowContainer>
    </Container>
  );
};

Note.propTypes = {
  type: PropTypes.string.isRequired,
  // Not required: legacy CALL_OUTCOME entries may have been saved with a null value.
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  date: PropTypes.objectOf(Date).isRequired,
  name: PropTypes.string.isRequired,
  reason: PropTypes.string,
  note: PropTypes.string,
  createdUserId: PropTypes.string.isRequired,
  coachId: PropTypes.string,
};

Note.defaultProps = {
  value: null,
  reason: '',
  note: '',
  coachId: '',
};

export default compose(
  observer,
)(Note);
